import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Receipt, FileSpreadsheet } from 'lucide-react'
import { utils, writeFileXLSX } from 'xlsx'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { toast } from '@/components/ui/Toast'
import { MyCompanyService } from '@/services/modules/MyCompanyService'
import type { Revenue, RevenueStatement } from '@/services/modules/RevenuesService'
import { brl, dateBR } from '@/lib/format'

export function MyCompanyInvoiceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [revenue, setRevenue] = useState<Revenue | null>(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const PAGE = 25

  const load = useCallback(() => {
    if (!id) return
    setLoading(true)
    MyCompanyService.invoice(id)
      .then(setRevenue)
      .catch(() => setRevenue(null))
      .finally(() => setLoading(false))
  }, [id])
  useEffect(() => { load() }, [load])

  if (loading) return <div className="text-sm text-gray-500">Carregando…</div>
  if (!revenue) return <div className="text-sm text-gray-500">Fatura não encontrada.</div>

  const rev = revenue
  const isBoleto = (rev.method ?? '').toUpperCase() === 'BOLETO'
  const statements = rev.statements ?? []

  const subtotal = statements.reduce((a, s) => a + (s.planAmount || 0), 0)
  const discounts = statements.reduce((a, s) => a + (s.discountAmount || 0), 0)
  const total = statements.reduce((a, s) => a + (s.amount || 0), 0)

  const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
  const sorted = [...statements].sort((a, b) => norm(a.subscriber?.name ?? '').localeCompare(norm(b.subscriber?.name ?? '')))
  const filtered = search.trim() ? sorted.filter(s => norm(s.subscriber?.name ?? '').includes(norm(search))) : sorted
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE))
  const safePage = Math.min(page, totalPages)
  const paged = filtered.slice((safePage - 1) * PAGE, safePage * PAGE)

  // Exporta o extrato (ordenado + filtrado) para .xlsx (igual /revenues).
  function exportStatements() {
    if (filtered.length === 0) {
      toast.error({ title: 'Nada para exportar', description: 'O extrato está vazio.' })
      return
    }
    const pct = (s: RevenueStatement) => (s.planAmount > 0 && s.discountAmount > 0 ? (s.discountAmount / s.planAmount) * 100 : 0)
    const aoa = [
      ['Parcela', 'Assinante', 'Cupom', 'Plano', 'Desconto', 'Desc. %', 'Total'],
      ...filtered.map((s) => [
        s.sequence === 9999 ? '' : s.sequence,
        s.subscriber?.name ?? '',
        s.couponCode ?? '',
        s.planAmount,
        s.discountAmount,
        s.discountAmount > 0 ? Number(pct(s).toFixed(2)) : 0,
        s.amount,
      ]),
    ]
    const ws = utils.aoa_to_sheet(aoa)
    const wb = utils.book_new()
    utils.book_append_sheet(wb, ws, 'Extrato')
    writeFileXLSX(wb, `extrato-fatura-${rev.code}.xlsx`)
  }

  const columns: DataTableColumn<RevenueStatement>[] = [
    { key: 'sequence',   header: 'Parcela', align: 'center', render: s => s.sequence === 9999 ? '—' : `#${s.sequence}` },
    { key: 'subscriber', header: 'Assinante', render: s => s.subscriber?.name ?? '—' },
    { key: 'coupon',     header: 'Cupom', align: 'center', render: s => s.couponCode ?? '—' },
    { key: 'plan',       header: 'Plano', align: 'right', render: s => s.formatted?.planAmount ?? brl(s.planAmount) },
    { key: 'discount',   header: 'Desconto', align: 'right', render: s => (s.discountAmount > 0 ? `- ${s.formatted?.discountAmount ?? brl(s.discountAmount)}` : '—') },
    { key: 'amount',     header: 'Total', align: 'right', render: s => <span className="font-medium text-gray-800">{s.formatted?.amount ?? brl(s.amount)}</span> },
  ]

  return (
    <>
      <PageHeader
        title={`Fatura ${rev.code}`}
        subtitle="Detalhes da fatura"
        crumbs={[{ label: 'Financeiro' }, { label: 'Faturas', to: '/my-company/invoices' }, { label: rev.code }]}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate('/my-company/invoices')} iconLeft={<ArrowLeft size={14} />}>Voltar</Button>
            {isBoleto && rev.bankSlipUrl && (
              <Button onClick={() => window.open(rev.bankSlipUrl!, '_blank', 'noopener,noreferrer')} iconLeft={<Receipt size={14} />}>
                Ver boleto
              </Button>
            )}
          </div>
        }
      />

      <Card className="mb-4">
        <CardHeader title="Resumo" />
        <CardBody>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm md:grid-cols-3">
            <Field label="Status" value={<StatusBadge status={rev.status} />} />
            <Field label="Valor" value={rev.formatted?.amount ?? brl(rev.amount)} />
            <Field label="Forma de pagamento" value={rev.method ?? '—'} />
            <Field label="Vencimento" value={rev.formatted?.dueDate ?? dateBR(rev.dueDate)} />
            <Field label="Pago em" value={rev.paidDate ? (rev.formatted?.paidDate ?? dateBR(rev.paidDate)) : '—'} />
            <Field label="Criada em" value={rev.formatted?.createdAt ?? '—'} />
          </dl>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Extrato" subtitle="Itens que compõem o valor desta fatura" />
        <CardBody>
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="w-full max-w-xs">
              {statements.length > 1 && (
                <Input placeholder="Filtrar por nome" value={search} onChange={e => { setSearch(e.target.value); setPage(1) }} clearable />
              )}
            </div>
            <Button variant="secondary" size="sm" onClick={exportStatements} iconLeft={<FileSpreadsheet size={14} />} disabled={statements.length === 0}>
              Exportar Excel
            </Button>
          </div>

          <DataTable columns={columns} rows={paged} getRowKey={s => s.id} emptyMessage="Nenhum item de extrato." />

          {filtered.length > PAGE && (
            <div className="mt-3 flex items-center justify-between text-sm text-gray-600">
              <span>{(safePage - 1) * PAGE + 1}–{Math.min(safePage * PAGE, filtered.length)} de {filtered.length}</span>
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm" disabled={safePage <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Anterior</Button>
                <span className="text-xs text-gray-500">Página {safePage} de {totalPages}</span>
                <Button variant="secondary" size="sm" disabled={safePage >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Próxima</Button>
              </div>
            </div>
          )}

          <div className="mt-8 flex justify-end border-t border-gray-100 pt-5">
            <dl className="w-full max-w-xs space-y-1.5 text-sm">
              <div className="flex justify-between text-gray-600"><dt>Subtotal</dt><dd>{brl(subtotal)}</dd></div>
              <div className="flex justify-between text-gray-600"><dt>Descontos</dt><dd className={discounts > 0 ? 'text-rose-600' : ''}>{discounts > 0 ? `- ${brl(discounts)}` : brl(0)}</dd></div>
              <div className="flex justify-between border-t border-gray-200 pt-1.5 font-semibold text-gray-800"><dt>Total</dt><dd>{brl(total)}</dd></div>
            </dl>
          </div>
        </CardBody>
      </Card>
    </>
  )
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-gray-500">{label}</dt>
      <dd className="mt-0.5 font-medium text-gray-800">{value}</dd>
    </div>
  )
}

export default MyCompanyInvoiceDetailPage
